/**
 * Browser-side twin of the API's timezone helpers. Attendance days are reckoned
 * in the organisation's zone, so when an admin logs hours on "the 12th" the
 * inputs must mean the 12th *there*, not in the admin's browser zone.
 */

const cache = new Map<string, Intl.DateTimeFormat>();

function fmt(tz: string): Intl.DateTimeFormat {
  let f = cache.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone: tz, hourCycle: 'h23',
      year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
    cache.set(tz, f);
  }
  return f;
}

export interface ZonedParts { year: number; month: number; day: number; hour: number; minute: number; second: number }

export function zonedParts(instant: Date, tz: string): ZonedParts {
  const o: Record<string, string> = {};
  for (const p of fmt(tz).formatToParts(instant)) o[p.type] = p.value;
  return { year: +o.year, month: +o.month, day: +o.day, hour: +o.hour % 24, minute: +o.minute, second: +o.second };
}

function offsetAt(instant: Date, tz: string): number {
  const p = zonedParts(instant, tz);
  return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) - Math.floor(instant.getTime() / 1000) * 1000;
}

/** The instant at which a local date-time occurs in the zone (DST-safe). */
export function zonedTimeToUtc(year: number, month: number, day: number, hour: number, minute: number, tz: string): Date {
  const guess = Date.UTC(year, month - 1, day, hour, minute, 0);
  const o1 = offsetAt(new Date(guess), tz);
  const o2 = offsetAt(new Date(guess - o1), tz);
  return new Date(guess - o2);
}

/** [start, end) of a YYYY-MM-DD day in the zone. */
export function dayBounds(dateKey: string, tz: string): { start: Date; end: Date } {
  const [y, m, d] = dateKey.split('-').map(Number);
  const start = zonedTimeToUtc(y, m, d, 0, 0, tz);
  const next = new Date(Date.UTC(y, m - 1, d + 1));
  const end = zonedTimeToUtc(next.getUTCFullYear(), next.getUTCMonth() + 1, next.getUTCDate(), 0, 0, tz);
  return { start, end };
}

/** "HH:MM" of an instant in the zone, for time inputs. */
export function zonedTimeInput(instant: Date, tz: string): string {
  const p = zonedParts(instant, tz);
  return `${String(p.hour).padStart(2, '0')}:${String(p.minute).padStart(2, '0')}`;
}

export function formatZonedTime(instant: Date | string, tz: string): string {
  return new Intl.DateTimeFormat(undefined, { timeZone: tz, hour: '2-digit', minute: '2-digit' }).format(new Date(instant));
}
